import { useEffect, useState } from "react";

/** COMPONENT */
import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ModalCustom from "../../../component/modalCustom/modalCustom";
import { notiError } from "../../../component/notification/notifications";

/** API */
import InheritanceTransferApi, {
  InheritanceTransferFile,
  InheritanceTransferLog,
} from "../../../api/inheritance-transfer/inheritance-transfer.api";

/** UTILS */
import { colors } from "../../../constants/colors";
import {
  INHERITANCE_LOG_ACTION_LABEL,
  inheritanceStatusBg,
  inheritanceStatusColor,
  inheritanceStatusLabel,
} from "../../../constants/inheritanceTransfer";
import { dateTimeLogToView } from "../../../utils/date.utils";
import { getApiErrorMessage, getApiErrorStatus } from "../../../utils/api-error.utils";
import { formatFileSize } from "../../../utils/common";
import {
  displayFileName,
  downloadInheritanceFile,
  openInheritanceFile,
} from "../inheritanceFile.utils";

/** STYLE */
import * as Style from "../inheritanceTransfer.style";

type Props = {
  id: number;
  inheritanceNo?: string;
  onClose: () => void;
};

/** ไฟล์ที่กำลังโหลดอยู่ — เก็บ action ด้วยเพื่อให้ spinner ขึ้นตรงปุ่มที่กด */
type FileBusy = { id: number; action: "view" | "download" };

export default function ModalInheritanceLog({ id, inheritanceNo, onClose }: Props) {
  const [logs, setLogs] = useState<InheritanceTransferLog[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [busy, setBusy] = useState<FileBusy | null>(null);

  useEffect(() => {
    /** กัน setState หลัง unmount ตอนผู้ใช้ปิด modal ก่อน response กลับมา */
    let isMounted = true;

    const loadLog = async () => {
      setIsLoading(true);
      try {
        const res = await InheritanceTransferApi.log(id);
        if (!isMounted) return;
        const rows = Array.isArray(res.data?.data) ? [...res.data.data] : [];
        // เรียงใหม่ -> เก่า เองด้วย ไม่พึ่ง order ที่ backend ส่งมา
        rows.sort(
          (a, b) => b.id_inheritance_transfer_log - a.id_inheritance_transfer_log
        );
        setLogs(rows);
      } catch (error) {
        if (!isMounted) return;
        setLogs([]);
        notiError(getApiErrorMessage(error, "ดึงประวัติการพิจารณาไม่สำเร็จ"));
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadLog();
    return () => {
      isMounted = false;
    };
  }, [id]);

  /** แถว CREATE คือตอนยื่นคำร้อง ยังไม่มีผลพิจารณา จึงใช้สีน้ำเงินตามดีไซน์ */
  const dotColor = (log: InheritanceTransferLog) =>
    log.action === "CREATE" ? colors.themeSecondColor : inheritanceStatusColor(log.result || "");

  /** 404 ของ backend ตอบ body ว่าง (filter ไม่มี branch 404) จึงต้องดูจาก status แล้วใส่ข้อความเอง */
  const notiFileError = (error: any, fallback: string) => {
    const status = getApiErrorStatus(error);
    notiError(status === 404 ? "ไม่พบไฟล์แนบที่ต้องการ" : getApiErrorMessage(error, fallback));
  };

  const handleView = async (file: InheritanceTransferFile) => {
    if (busy) return;

    // ต้องเปิดแท็บตรงนี้ ตอนยังอยู่ใน user gesture ไม่งั้นโดน popup blocker หลัง await
    const tab = window.open("", "_blank");
    if (!tab) {
      notiError("เบราว์เซอร์บล็อกการเปิดแท็บใหม่ กรุณาอนุญาต popup หรือกดดาวน์โหลดแทน");
      return;
    }

    setBusy({ id: file.id_inheritance_transfer_file, action: "view" });
    try {
      await openInheritanceFile(file, tab);
    } catch (error) {
      tab.close();
      notiFileError(error, "เปิดไฟล์แนบไม่สำเร็จ");
    } finally {
      setBusy(null);
    }
  };

  const handleDownload = async (file: InheritanceTransferFile) => {
    if (busy) return;

    setBusy({ id: file.id_inheritance_transfer_file, action: "download" });
    try {
      await downloadInheritanceFile(file);
    } catch (error) {
      notiFileError(error, "ดาวน์โหลดไฟล์แนบไม่สำเร็จ");
    } finally {
      setBusy(null);
    }
  };

  const renderFile = (file: InheritanceTransferFile) => {
    const fileId = file.id_inheritance_transfer_file;
    const isViewing = busy?.id === fileId && busy?.action === "view";
    const isDownloading = busy?.id === fileId && busy?.action === "download";
    const isDisabled = !!busy;

    return (
      <Box
        key={fileId}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mt: 1,
          px: 1.5,
          py: 1,
          borderRadius: "8px",
          border: `1px solid ${colors.lightGray}`,
          backgroundColor: colors.white,
        }}
      >
        <InsertDriveFileOutlinedIcon sx={{ fontSize: 20, color: colors.textLightGray }} />
        {/* ตัด timestamp ที่ backend เติมหน้าชื่อไฟล์ออก ผู้ใช้ไม่ต้องเห็น */}
        <Typography sx={{ fontSize: 16, flex: 1, minWidth: 0, wordBreak: "break-all" }}>
          {displayFileName(file.file_name)}
        </Typography>
        {file.file_size ? (
          <Typography sx={{ fontSize: 14, color: colors.textLightGray, whiteSpace: "nowrap" }}>
            {formatFileSize(file.file_size)}
          </Typography>
        ) : null}
        <Tooltip title="เปิดดูไฟล์">
          <span>
            <IconButton size="small" disabled={isDisabled} onClick={() => handleView(file)}>
              {isViewing ? (
                <CircularProgress size={16} sx={{ color: colors.themeMainColor }} />
              ) : (
                <VisibilityOutlinedIcon sx={{ fontSize: 20, color: colors.textLightGray }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="ดาวน์โหลดไฟล์">
          <span>
            <IconButton size="small" disabled={isDisabled} onClick={() => handleDownload(file)}>
              {isDownloading ? (
                <CircularProgress size={16} sx={{ color: colors.themeMainColor }} />
              ) : (
                <DownloadOutlinedIcon sx={{ fontSize: 20, color: colors.themeSecondColor }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  const renderLog = (log: InheritanceTransferLog, index: number) => {
    const actionLabel = INHERITANCE_LOG_ACTION_LABEL[log.action] || log.action;
    const actionDate = log.action_date || log.created_date;

    return (
      <Box
        key={log.id_inheritance_transfer_log}
        sx={{
          display: "flex",
          gap: 1.5,
          py: 2,
          // ใช้ borderTop ไม่ใช้ MUI Divider เพราะ custom.css override margin/สีของ Divider ทั้งโปรเจกต์
          borderTop: index === 0 ? undefined : `1px solid ${colors.lightGray}`,
        }}
      >
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: dotColor(log),
            flexShrink: 0,
            mt: "7px",
          }}
        />
        <Box minWidth={0} sx={{ flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 16, fontWeight: 600 }}>{actionLabel}</Typography>
              {/* result เป็น null เมื่อ action = CREATE จึงโชว์เฉพาะตอนพิจารณา */}
              {log.result && (
                <Style.StatusChip
                  chipColor={inheritanceStatusColor(log.result)}
                  chipBg={inheritanceStatusBg(log.result)}
                  sx={{ fontSize: 14, minWidth: "unset", px: 1.5, py: "2px" }}
                >
                  {inheritanceStatusLabel(log.result)}
                </Style.StatusChip>
              )}
            </Box>
            <Typography sx={{ fontSize: 14, color: colors.textLightGray, whiteSpace: "nowrap" }}>
              {actionDate ? dateTimeLogToView(actionDate) : "-"}
            </Typography>
          </Box>

          {/* action_name เป็น snapshot ตอนทำ ใช้แสดงได้เลยไม่ต้อง join user */}
          <Typography sx={{ fontSize: 14, color: colors.textLightGray, mt: 0.5 }}>
            {`โดย ${log.action_name || "-"}`}
          </Typography>

          {log.note && (
            <Typography
              sx={{
                fontSize: 14,
                whiteSpace: "pre-line",
                wordBreak: "break-word",
                mt: 0.5,
              }}
            >
              {log.note}
            </Typography>
          )}

          {log.files?.length > 0 && <Box sx={{ mt: 0.5 }}>{log.files.map(renderFile)}</Box>}
        </Box>
      </Box>
    );
  };

  const renderBody = () => (
    <Box sx={{ px: { xs: 2, md: 3 }, pb: 1 }}>
      {inheritanceNo && (
        <Typography sx={{ fontSize: 16, color: colors.textLightGray, textAlign: "center", mb: 2 }}>
          {inheritanceNo}
        </Typography>
      )}

      <Box
        sx={{
          border: `1px solid ${colors.lightGray}`,
          borderRadius: "8px",
          backgroundColor: colors.white,
          px: { xs: 2, md: 3 },
        }}
      >
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={32} sx={{ color: colors.themeMainColor }} />
          </Box>
        ) : logs.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 4 }}>
            <Typography sx={{ color: colors.textLightGray }}>ยังไม่มีการพิจารณาคำร้องนี้</Typography>
          </Box>
        ) : (
          <Box className="custom-scroll" sx={{ maxHeight: 420, overflowY: "auto" }}>
            {logs.map(renderLog)}
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <ModalCustom
      // ต้องเป็น true เสมอ + ให้ parent conditional render (ค่า false จะ fall back ไปอ่าน redux)
      isShowProps
      // ปิด modal เองผ่าน onClose ไม่ให้ไป reset redux modal.isShow ของ modal อื่นในหน้าเดียวกัน
      autoResetModal={false}
      size="lg"
      closeButton
      title="ประวัติการพิจารณา"
      component={renderBody()}
      // ไม่ส่ง onSubmit/textBtnConfirm footer จึงไม่มีปุ่ม เหลือแค่ X มุมขวาบนตามดีไซน์
      onClose={onClose}
    />
  );
}
